import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = useMemo(() => comics.find(c => c.id === Number(id)), [id]);
  const [activeTab, setActiveTab] = useState<'chapters' | 'info'>('chapters');

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300 w-12 h-12 flex items-center justify-center mx-auto"></i>
            <h2 className="text-xl font-semibold text-foreground-900 mt-4">漫画未找到</h2>
            <Link to="/" className="inline-block mt-4 text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catName = categories.find(c => c.slug === comic.category)?.name || comic.category;
  const related = comics.filter(c => c.category === comic.category && c.id !== comic.id).slice(0, 6);

  const chapterList = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(new Date(comic.updateTime).getTime() - i * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  }));

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画无删减下拉式`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2026-01-01',
    dateModified: comic.updateTime,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '韩漫大全',
    },
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

        <div className="bg-background-100 border-b border-background-200">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: catName, href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-shrink-0 mx-auto md:mx-0">
              <div className="w-48 md:w-56 rounded-lg overflow-hidden shadow-sm">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - 韩国漫画无删减下拉式`}
                  className="w-full aspect-[3/4] object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                  comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
                <span className="text-xs bg-accent-100 text-accent-700 font-semibold px-2.5 py-1 rounded-full">
                  ★ {comic.rating}
                </span>
                <span className="text-xs bg-background-200 text-foreground-600 px-2.5 py-1 rounded-full">
                  {catName}
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">
                作者：{comic.author} · 共 {comic.chapters} 话 · {(comic.views / 10000).toFixed(0)} 万次阅读
              </p>

              <div className="flex items-center gap-2 mb-6 flex-wrap">
                <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-play-circle-line w-4 h-4 flex items-center justify-center"></i>
                  开始阅读第一话
                </button>
                <button className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line w-4 h-4 flex items-center justify-center"></i>
                  收藏
                </button>
                <button className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center"></i>
                  分享
                </button>
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed bg-background-100 rounded-lg p-4">
                {comic.description}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-8">
          <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1 border border-background-200 mb-6 w-fit">
            <button
              onClick={() => setActiveTab('chapters')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'chapters' ? 'bg-primary-500 text-white' : 'text-foreground-600'
              }`}
            >
              章节列表
            </button>
            <button
              onClick={() => setActiveTab('info')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'info' ? 'bg-primary-500 text-white' : 'text-foreground-600'
              }`}
            >
              作品信息
            </button>
          </div>

          {activeTab === 'chapters' && (
            <div className="space-y-1">
              {chapterList.map(ch => (
                <div
                  key={ch.num}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-200 hover:bg-primary-50/30 transition-all cursor-pointer group"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                      {ch.title}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-400">{ch.date}</span>
                </div>
              ))}
              {comic.chapters > 20 && (
                <div className="text-center pt-4">
                  <p className="text-sm text-foreground-500">
                    共 {comic.chapters} 话，仅显示最近 20 话
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'info' && (
            <div className="bg-background-50 border border-background-200 rounded-lg p-6">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-xs text-foreground-500 mb-1">作品名称</dt>
                  <dd className="text-sm font-medium text-foreground-900">{comic.title}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500 mb-1">韩文原名</dt>
                  <dd className="text-sm font-medium text-foreground-900">{comic.titleKo}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500 mb-1">作者</dt>
                  <dd className="text-sm font-medium text-foreground-900">{comic.author}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500 mb-1">分类</dt>
                  <dd className="text-sm font-medium text-foreground-900">{catName}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500 mb-1">状态</dt>
                  <dd className="text-sm font-medium text-foreground-900">{comic.status}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500 mb-1">总话数</dt>
                  <dd className="text-sm font-medium text-foreground-900">{comic.chapters} 话</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500 mb-1">评分</dt>
                  <dd className="text-sm font-medium text-accent-600">★ {comic.rating}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500 mb-1">阅读量</dt>
                  <dd className="text-sm font-medium text-foreground-900">{(comic.views / 10000).toFixed(0)} 万</dd>
                </div>
              </dl>
            </div>
          )}
        </div>

        {related.length > 0 && (
          <section className="py-10 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4" data-product-shop="">
                {related.map(r => (
                  <Link
                    key={r.id}
                    to={`/comic/${r.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2">
                      <img
                        src={r.cover}
                        alt={r.title}
                        title={`${r.title} - 韩国漫画下拉式`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="inline-block bg-accent-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                          {r.rating}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-xs font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {r.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}